import {
  canPublish,
  PLACE_STATUSES,
  PlaceUpdateSchema,
  type PlaceUpdateInput,
} from '@antoja/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { t } from '../i18n';
import { DatabaseRuleError, type ConsoleApi, type PlaceDetail } from '../lib/api';
import { Icon } from '../icons';
import { MenusCard } from '../menus/menus-card';
import { hrefFor } from '../shell/route';
import { controlClass, selectClass } from '../styles';
import { Button, Card, Field, FormFooter, Notice, PageHeader } from '../ui';
import { formatDate, statusLabel } from './format';
import { AppLabel, StateWithIcon } from './labels';

function errorMessage(error: unknown): string {
  if (error instanceof DatabaseRuleError) return t(`dbError.${error.hint}`);
  return t('place.saveError');
}

export function PlaceScreen({
  api,
  id,
  canEdit,
}: {
  api: ConsoleApi;
  id: string;
  canEdit: boolean;
}) {
  const place = useQuery({ queryKey: ['place', id], queryFn: () => api.getPlace(id) });

  return (
    <>
      {/* Close to the title it belongs to: 12 px instead of the 24 px between blocks. */}
      <a
        href={hrefFor({ name: 'places' })}
        className="-mb-3 -ml-2 inline-flex min-h-11 items-center gap-1.5 justify-self-start rounded-lg px-2 text-sm text-muted hover:text-ink"
      >
        <Icon name="back" size={16} />
        {t('places.title')}
      </a>
      {place.isPending ? (
        <p role="status" className="text-muted">
          {t('place.loading')}
        </p>
      ) : null}
      {place.isError ? (
        <div role="alert" className="grid justify-items-start gap-3">
          <p>{t('place.error')}</p>
          <Button onClick={() => void place.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : null}
      {place.isSuccess && !place.data ? <PageHeader title={t('place.notFound')} /> : null}
      {place.isSuccess && place.data ? (
        <PlaceView key={place.data.id} api={api} place={place.data} canEdit={canEdit} />
      ) : null}
    </>
  );
}

function PlaceView({
  api,
  place,
  canEdit,
}: {
  api: ConsoleApi;
  place: PlaceDetail;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PlaceUpdateInput>({
    name: place.name,
    address: place.address ?? '',
    zone_id: place.zoneId,
    status: place.status,
    price_level: place.priceLevel,
    google_place_id: place.googlePlaceId ?? '',
  });
  const [invalid, setInvalid] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  // The "En la app" action (Quitar or Publicar) and the confirmation's "Sí, quitar".
  const actionRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  // After publishing or unpublishing, focus goes back to the action once the data reloads.
  const returnFocus = useRef(false);
  const zones = useQuery({ queryKey: ['zones', 'pilot'], queryFn: () => api.listPilotZones() });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['place', place.id] });
    void queryClient.invalidateQueries({ queryKey: ['places'] });
  };
  const save = useMutation({
    mutationFn: (patch: Parameters<ConsoleApi['updatePlace']>[1]) =>
      api.updatePlace(place.id, patch),
    onSuccess: refresh,
  });
  const publish = useMutation({
    mutationFn: (value: boolean) => api.setPublished(place.id, value),
    onSuccess: () => {
      returnFocus.current = true;
      setConfirmUnpublish(false);
      refresh();
    },
  });

  useEffect(() => {
    if (confirmUnpublish) confirmRef.current?.focus();
  }, [confirmUnpublish]);
  useEffect(() => {
    if (!returnFocus.current) return;
    returnFocus.current = false;
    actionRef.current?.focus();
  }, [place.isPublished]);

  function closeConfirm() {
    setConfirmUnpublish(false);
    actionRef.current?.focus();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = PlaceUpdateSchema.safeParse(form);
    setInvalid(!parsed.success);
    if (parsed.success) save.mutate(parsed.data);
  }

  const set = <K extends keyof PlaceUpdateInput>(key: K, value: PlaceUpdateInput[K]) => {
    save.reset(); // "Cambios guardados" must never sit next to unsaved edits
    setForm((current) => ({ ...current, [key]: value }));
  };
  const eligible = canPublish(place.facts);

  const footerStatus = (
    <>
      {invalid ? <Notice id="place-invalid">{t('place.invalid')}</Notice> : null}
      {save.isError ? <Notice>{errorMessage(save.error)}</Notice> : null}
    </>
  );

  return (
    <>
      <PageHeader
        title={place.name}
        subtitle={[place.zoneName ?? t('places.noZone'), statusLabel(place.status)].join(' · ')}
      />
      <div className="grid items-start gap-6 min-[1100px]:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <Card title={t('place.data')}>
          <form className="@container grid gap-5" onSubmit={submit} noValidate>
            {!canEdit ? (
              <p id="place-read-only" className="text-sm text-muted">
                {t('place.readOnly')}
              </p>
            ) : null}
            <fieldset
              disabled={!canEdit || save.isPending}
              aria-describedby={canEdit ? undefined : 'place-read-only'}
              className="grid min-w-0 gap-5"
            >
              {/* Wide enough: Barrio, Estado and Precio share a row; Estado gets the most room. */}
              <div className="grid gap-x-4 gap-y-5 @min-[500px]:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,0.9fr)]">
                {/* The database unpublishes a renamed place and resets its location
                    verification; the note says so before saving. */}
                <Field
                  label={t('place.name')}
                  htmlFor="place-name"
                  hint={t('place.nameHint')}
                  hintId="place-name-hint"
                  className="@min-[500px]:col-span-3"
                >
                  <input
                    id="place-name"
                    value={form.name}
                    onChange={(event) => set('name', event.target.value)}
                    aria-invalid={invalid}
                    aria-describedby={invalid ? 'place-invalid place-name-hint' : 'place-name-hint'}
                    className={controlClass}
                  />
                </Field>
                <Field
                  label={t('place.address')}
                  htmlFor="place-address"
                  className="@min-[500px]:col-span-3"
                >
                  <input
                    id="place-address"
                    value={form.address ?? ''}
                    onChange={(event) => set('address', event.target.value)}
                    className={controlClass}
                  />
                </Field>
                <Field label={t('places.zone')} htmlFor="place-zone">
                  <select
                    id="place-zone"
                    value={form.zone_id ?? ''}
                    onChange={(event) =>
                      set('zone_id', event.target.value ? Number(event.target.value) : null)
                    }
                    className={selectClass}
                  >
                    <option value="">{t('places.noZone')}</option>
                    {zones.data?.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t('place.status')} htmlFor="place-status">
                  <select
                    id="place-status"
                    value={form.status}
                    onChange={(event) =>
                      set('status', event.target.value as PlaceUpdateInput['status'])
                    }
                    className={selectClass}
                  >
                    {PLACE_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {statusLabel(status)}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t('place.priceLevel')} htmlFor="place-price">
                  <select
                    id="place-price"
                    value={form.price_level ?? ''}
                    onChange={(event) =>
                      set('price_level', event.target.value ? Number(event.target.value) : null)
                    }
                    className={selectClass}
                  >
                    <option value="">{t('place.priceUnknown')}</option>
                    {[1, 2, 3, 4].map((level) => (
                      <option key={level} value={level}>
                        {'$'.repeat(level)}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {/* The two outside IDs belong together: their own row, after a divider. */}
              <div className="grid gap-x-4 gap-y-5 border-t border-line pt-5 @min-[500px]:grid-cols-2">
                {/* A new Google id resets location and business status (the database does it). */}
                <Field
                  label={t('place.googleId')}
                  htmlFor="place-google"
                  hint={t('place.googleIdHint')}
                  hintId="place-google-hint"
                >
                  <input
                    id="place-google"
                    value={form.google_place_id ?? ''}
                    onChange={(event) => set('google_place_id', event.target.value)}
                    aria-describedby="place-google-hint"
                    spellCheck={false}
                    className={controlClass}
                  />
                </Field>
                {/* Comes from the FSQ import only: shown, never sent on save. */}
                <Field
                  label={t('place.fsqId')}
                  htmlFor="place-fsq"
                  hint={t('place.fsqIdHint')}
                  hintId="place-fsq-hint"
                >
                  <input
                    id="place-fsq"
                    value={place.fsqId ?? ''}
                    readOnly
                    aria-describedby="place-fsq-hint"
                    spellCheck={false}
                    className={`${controlClass} read-only:border-line read-only:bg-canvas`}
                  />
                </Field>
              </div>
            </fieldset>
            {canEdit ? (
              <FormFooter status={footerStatus} live={save.isSuccess ? t('place.saved') : ''}>
                <Button type="submit" variant="primary" disabled={save.isPending}>
                  {save.isPending ? t('place.saving') : t('place.save')}
                </Button>
              </FormFooter>
            ) : null}
          </form>
        </Card>

        <div className="grid gap-6">
          <Card title={t('place.verification')}>
            <ul className="grid divide-y divide-line">
              {(['location', 'business_status'] as const).map((field) => {
                const fact = place.facts.find((item) => item.field === field);
                const details = fact
                  ? [
                      t(fact.family_count === 1 ? 'place.families.one' : 'place.families', {
                        count: fact.family_count,
                      }),
                      fact.expires_at
                        ? t('place.expires', { date: formatDate(fact.expires_at) })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : null;
                return (
                  <li key={field} className="grid gap-1 py-3 first:pt-0 last:pb-0">
                    <span className="text-[14.5px] font-semibold">{t(`fact.${field}`)}</span>
                    <span className="text-sm">
                      <StateWithIcon
                        state={fact ? fact.status : 'none'}
                        label={fact ? t(`factStatus.${fact.status}`) : t('place.factMissing')}
                      />
                    </span>
                    {details ? <span className="text-[13px] text-muted">{details}</span> : null}
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card title={t('place.inApp')}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[14.5px] font-medium">
                <AppLabel isPublished={place.isPublished} hiddenUntil={place.hiddenUntil} />
              </p>
              {/* Stays in place while the confirmation is open, so focus can come back to it. */}
              {canEdit && place.isPublished ? (
                <Button
                  ref={actionRef}
                  aria-expanded={confirmUnpublish}
                  aria-controls={confirmUnpublish ? 'unpublish-confirm' : undefined}
                  onClick={() => (confirmUnpublish ? closeConfirm() : setConfirmUnpublish(true))}
                >
                  {t('place.unpublish')}
                </Button>
              ) : null}
              {canEdit && !place.isPublished ? (
                <Button
                  ref={actionRef}
                  disabled={!eligible || publish.isPending}
                  aria-describedby={eligible ? undefined : 'publish-help'}
                  onClick={() => publish.mutate(true)}
                >
                  {t('place.publish')}
                </Button>
              ) : null}
            </div>
            {!place.isPublished && !eligible ? (
              <p id="publish-help" className="text-sm text-muted">
                {t('place.publishNeedsVerification')}
              </p>
            ) : null}
            {canEdit && place.isPublished && confirmUnpublish ? (
              <div id="unpublish-confirm" className="grid gap-3 border-t border-line pt-4">
                <p className="text-sm">{t('place.unpublishConfirm')}</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    ref={confirmRef}
                    disabled={publish.isPending}
                    onClick={() => publish.mutate(false)}
                  >
                    {t('place.unpublishYes')}
                  </Button>
                  <Button onClick={closeConfirm}>{t('common.cancel')}</Button>
                </div>
              </div>
            ) : null}
            {publish.isError ? <Notice>{errorMessage(publish.error)}</Notice> : null}
          </Card>
        </div>

        <MenusCard api={api} placeId={place.id} canEdit={canEdit} />
      </div>
    </>
  );
}
